import { useBusinessEmail } from "@/lib/email";
import { pic } from "@/lib/responsive-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock, ExternalLink, Sparkles } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { TravelFeeNote } from "@/components/TravelFeeNote";
import { useI18n } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";
const contactHero = pic("contact-hero");
import { LeadflowForm } from "@/components/LeadflowForm";

const VALID_SERVICES = ["roofing", "solar", "both", "repair", "storm", "other"] as const;
type ServiceType = (typeof VALID_SERVICES)[number];

export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): { service?: ServiceType } => {
    const s = search.service;
    return typeof s === "string" && (VALID_SERVICES as readonly string[]).includes(s)
      ? { service: s as ServiceType }
      : {};
  },
  head: () => ({
    meta: [
      { title: "Contact AMR 3 | Roofing & Solar Consultation in NM" },
      {
        name: "description",
        content:
          "Request your roofing or solar consultation in Albuquerque or anywhere in New Mexico. Call (505) 868-0847 or send your project details online.",
      },
      { property: "og:title", content: "Contact AMR 3 | Roofing & Solar Consultation" },
      {
        property: "og:description",
        content:
          "Request your roofing or solar consultation across New Mexico — fast response, no obligation.",
      },
      { property: "og:url", content: "https://www.amr3designconstruction.com/contact" },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-contact.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-contact.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Contact AMR 3 — Roofing & Solar Consultation" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-contact.jpg" },
      { name: "twitter:title", content: "Contact AMR 3 | Roofing & Solar Consultation" },
      { name: "twitter:description", content: "Request your roofing or solar consultation across New Mexico — fast response, no obligation." },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/contact" }],
  }),
  component: ContactPage,
});


function ContactPage() {
  const { t } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();
  const email = useBusinessEmail();
  const infoCards = [
    { icon: Phone, label: "(505) 868-0847", href: "tel:+15058680847" },
    { icon: Mail, label: email.label, href: email.href },
    { icon: MapPin, label: t("contact.info.address") },
    { icon: Clock, label: t("contact.info.hours") },
  ];

  return (
    <>
      {/* Hero header */}
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={contactHero.src}
          srcSet={contactHero.srcSet}
          sizes="100vw"
          alt="AMR 3 contractor greeting a homeowner outside an Albuquerque home with a new roof and solar panels"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-20 h-full w-full object-cover object-top"
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(120deg, oklch(0.18 0.03 50 / 0.80) 0%, oklch(0.18 0.03 50 / 0.58) 55%, oklch(0.18 0.03 50 / 0.30) 100%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.contact") }]} />
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-background/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> {t("contact.badge")}
          </span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-bold leading-tight text-background sm:text-5xl lg:text-6xl">
            {t("contact.h1")}
          </h1>
        </div>
      </section>

      {/* Contact info cards row */}
      <section className="bg-background pt-12 sm:pt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {infoCards.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <div className="flex items-center gap-3 rounded-md border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium text-primary">{c.label}</span>
                </div>
              );
              return c.href ? (
                <a key={i} href={c.href} className="block">
                  {inner}
                </a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form card */}
      <section className="bg-background py-12 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div id="project-form" className="scroll-mt-20 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
            <div className="border-b border-border bg-gradient-to-br from-secondary/60 via-card to-card px-5 py-5 sm:px-10 sm:py-8">
              <div className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="font-serif text-xl font-bold text-primary sm:text-3xl">
                    {t("contact.form.title")}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {t("contact.form.sub")}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-6"><LeadflowForm /></div>

          </div>
        </div>
      </section>

      {/* Find Us — map */}
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-serif text-3xl font-bold text-primary sm:text-4xl">
            {t("contact.find.title")}
          </h2>

          <div className="mt-10 overflow-hidden rounded-md border border-border shadow-md">
            <iframe
              title="AMR 3 Design & Construction service area — Albuquerque, New Mexico"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4680.464125686354!2d-106.61607635759!3d35.15315244883415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872275915ec31311%3A0x5be5f3e93e0cab4d!2sAMR%203%20Design%20%26%20Construction%20LLC!5e0!3m2!1sfr!2stn!4v1778686890668!5m2!1sfr!2stn"
              width="100%"
              height="460"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block h-[460px] w-full border-0"
            />
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://www.google.com/maps?cid=6621967010212129613"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent-strong hover:underline"
            >
              <MapPin className="h-4 w-4" /> {t("contact.find.maps")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <div className="hidden">
        <Link to="/">Home</Link>
      </div>

    </>
  );
}
